import { Info, Lock, TriangleAlert } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { isApiError } from '../api';
import { MAX_REASON_LENGTH, MIN_REASON_LENGTH, setOpsReason, toHeaderSafe, useOpsReason } from '../api/opsReason';
import { ErrorState } from './States';
import { errorHint } from './Write';

/**
 * Asks for the reason the server requires before running an observability query that
 * names business objects (spec §122). The reason is audited with every query and
 * kept in memory for this tab only.
 */
export function ReasonPrompt({ onSubmitted, onCancel, intro }: { onSubmitted: () => void; onCancel?: () => void; intro?: string }) {
  const { t } = useTranslation();
  const current = useOpsReason();
  const [text, setText] = useState(current ?? '');
  const id = useId();
  const safe = toHeaderSafe(text);
  const valid = safe.length >= MIN_REASON_LENGTH;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setOpsReason(safe);
    onSubmitted();
  };

  return (
    <form className="flex flex-col gap-3 rounded-lg border border-warning/30 bg-warning-soft/60 p-4 text-sm" onSubmit={submit} aria-labelledby={`${id}-title`}>
      <h3 id={`${id}-title`} className="flex items-center gap-2 font-semibold">
        <Lock className="size-4 text-warning" aria-hidden="true" /> {t('reason.title')}
      </h3>
      <p className="text-foreground/85">{intro ?? t('reason.intro')}</p>
      <div className="grid gap-1.5">
        <Label htmlFor={`${id}-reason`}>{t('reason.label', { min: MIN_REASON_LENGTH })}</Label>
        <Input
          id={`${id}-reason`}
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={MAX_REASON_LENGTH}
          autoComplete="off"
          placeholder={t('reason.placeholder')}
          className="bg-background"
          required
        />
      </div>
      {text && safe !== text.trim() ? <p className="text-xs text-muted-foreground">{t('reason.sentAs', { value: safe })}</p> : null}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" size="sm" disabled={!valid}>
          {t('reason.submit')}
        </Button>
        {onCancel ? (
          <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
            {t('action.cancel')}
          </Button>
        ) : null}
      </div>
    </form>
  );
}

/** Shows (and lets the operator change or clear) the reason attached to observability queries. */
export function OpsReasonBar() {
  const { t } = useTranslation();
  const reason = useOpsReason();
  const [editing, setEditing] = useState(false);
  if (editing) return <ReasonPrompt onSubmitted={() => setEditing(false)} onCancel={() => setEditing(false)} intro={t('reason.barIntro')} />;
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border bg-muted/40 px-3 py-2 text-sm" role="status">
      <Info className="size-4 shrink-0 text-info" aria-hidden="true" />
      {reason ? (
        <>
          <span className="min-w-0 break-words">
            {t('reason.current')} <strong className="font-medium">{reason}</strong>
          </span>
          <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={() => setEditing(true)}>
            {t('reason.change')}
          </Button>
          <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={() => setOpsReason(null)}>
            {t('reason.clear')}
          </Button>
        </>
      ) : (
        <>
          <span className="text-muted-foreground">{t('reason.none')}</span>
          <Button type="button" variant="link" size="sm" className="h-auto p-0" onClick={() => setEditing(true)}>
            {t('reason.set')}
          </Button>
        </>
      )}
    </div>
  );
}

/** A backend that is simply not wired in this environment (e.g. no Tempo URL). */
export function isNotConfigured(error: unknown): boolean {
  return isApiError(error) && error.code === 'BACKEND_NOT_CONFIGURED';
}

type NotConfiguredSource = 'metrics' | 'logs' | 'traces' | 'alerts' | 'slo' | 'other';

/** Which kind of data source the server says is missing ("trace backend is not configured"), never its internal name. */
function missingSource(message: string): NotConfiguredSource {
  const m = message.toLowerCase();
  if (m.includes('metric')) return 'metrics';
  if (m.includes('log')) return 'logs';
  if (m.includes('trace')) return 'traces';
  if (m.includes('alert')) return 'alerts';
  if (m.includes('slo')) return 'slo';
  return 'other';
}

/**
 * Error display for reads. Handles the codes an operator can act on:
 * REASON_REQUIRED (prompt, then retry), BACKEND_NOT_CONFIGURED (explain), MFA and
 * permission problems (hint); everything else falls back to `ErrorState`.
 */
export function DataError({ error, onRetry, title }: { error: unknown; onRetry?: () => void; title?: string }) {
  const { t } = useTranslation();
  if (isApiError(error) && error.code === 'REASON_REQUIRED' && onRetry) {
    return <ReasonPrompt onSubmitted={onRetry} />;
  }
  if (isNotConfigured(error) && isApiError(error)) {
    return (
      <div className="flex gap-3 rounded-lg border border-info/25 bg-info-soft p-4 text-sm" role="note">
        <Info className="mt-0.5 size-4 shrink-0 text-info" aria-hidden="true" />
        <div className="flex flex-col gap-1">
          <strong className="font-semibold">{t('reason.notConfigured')}</strong>
          <p className="text-foreground/85">{t('reason.notConfiguredBody', { what: t(`reason.sources.${missingSource(error.message)}`) })}</p>
          <p className="text-xs text-muted-foreground">{t('write.meta', { code: error.code, requestId: error.requestId })}</p>
        </div>
      </div>
    );
  }
  const hint = isApiError(error) ? errorHint(error.code) : null;
  return (
    <div className="flex flex-col gap-2">
      <ErrorState error={error} onRetry={onRetry} title={title} />
      {hint ? (
        <p className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning-soft px-3 py-2 text-xs">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0 text-warning" aria-hidden="true" /> <span>{hint}</span>
        </p>
      ) : null}
    </div>
  );
}
